import type { Chain, Asset, KeyShare, ChainType } from "../shared/types";
import { getChainAdapter } from "./chains/adapter";
import { registerWalletAddress } from "./chains/chain-proxy-client";
import { publicKeyToBtcLegacyAddress } from "./chains/btcAdapter";
import { publicKeyToLtcLegacyAddress } from "./chains/ltcAdapter";

export interface AccountRow {
  keyId: string;
  address: string;
  addressType: ChainType;
  btcAddrType?: "segwit" | "legacy";
  label: string;
  chain: Chain;
  assets: Asset[];
}

export function buildAccountRows(
  keys: KeyShare[],
  chains: Chain[],
  assets: Asset[]
): AccountRow[] {
  const rows: AccountRow[] = [];
  const evmChains = chains.filter((c) => c.type === "evm");
  const btcChains = chains.filter((c) => c.type === "btc");
  const solanaChains = chains.filter((c) => c.type === "solana");
  const xrpChains = chains.filter((c) => c.type === "xrp");

  for (const key of keys) {
    if (!key.publicKey || !key.enabled) continue;
    // publicKey is stored as hex (SEC1 uncompressed)
    const pubKeyHex = key.publicKey;

    const evmAdapter = getChainAdapter("evm");
    if (evmChains.length > 0) {
      const evmAddr = evmAdapter.deriveAddress(pubKeyHex);
      for (const chain of evmChains) {
        const chainAssets = assets.filter((a) => a.chainId === chain.id);
        rows.push({
          keyId: key.id,
          address: evmAddr,
          addressType: "evm",
          label: chain.displayName,
          chain,
          assets: chainAssets,
        });
      }
    }

    const btcAdapter = getChainAdapter("btc");
    for (const chain of btcChains) {
      const testnet = chain.displayName.toLowerCase().includes("testnet");
      const chainAssets = assets.filter((a) => a.chainId === chain.id);
      rows.push({
        keyId: key.id,
        address: btcAdapter.deriveAddress(pubKeyHex, { testnet }),
        addressType: "btc",
        btcAddrType: "segwit",
        label: `${chain.displayName} (SegWit)`,
        chain,
        assets: chainAssets,
      });
      rows.push({
        keyId: key.id,
        address: publicKeyToBtcLegacyAddress(pubKeyHex, testnet),
        addressType: "btc",
        btcAddrType: "legacy",
        label: `${chain.displayName} (Legacy)`,
        chain,
        assets: chainAssets,
      });
    }

    if (solanaChains.length > 0) {
      const solanaAdapter = getChainAdapter("solana");
      let solanaAddr: string;
      try {
        solanaAddr = solanaAdapter.deriveAddress(key.eddsaPublicKey);
      } catch {
        continue;
      }
      for (const chain of solanaChains) {
        const chainAssets = assets.filter((a) => a.chainId === chain.id);
        rows.push({
          keyId: key.id,
          address: solanaAddr,
          addressType: "solana",
          label: chain.displayName,
          chain,
          assets: chainAssets,
        });
      }
    }

    const bchChains = chains.filter((c) => c.type === "bch");
    if (bchChains.length > 0) {
      const bchAdapt = getChainAdapter("bch");
      for (const chain of bchChains) {
        const testnet = chain.displayName.toLowerCase().includes("testnet");
        const chainAssets = assets.filter((a) => a.chainId === chain.id);
        rows.push({
          keyId: key.id,
          address: bchAdapt.deriveAddress(pubKeyHex, { testnet }),
          addressType: "bch",
          label: chain.displayName,
          chain,
          assets: chainAssets,
        });
      }
    }

    const dogeChains = chains.filter((c) => c.type === "doge");
    if (dogeChains.length > 0) {
      const dogeAdapt = getChainAdapter("doge");
      for (const chain of dogeChains) {
        const chainAssets = assets.filter((a) => a.chainId === chain.id);
        rows.push({
          keyId: key.id,
          address: dogeAdapt.deriveAddress(pubKeyHex),
          addressType: "doge",
          label: chain.displayName,
          chain,
          assets: chainAssets,
        });
      }
    }

    const ltcChains = chains.filter((c) => c.type === "ltc");
    if (ltcChains.length > 0) {
      const ltcAdapter = getChainAdapter("ltc");
      for (const chain of ltcChains) {
        const testnet = chain.displayName.toLowerCase().includes("testnet");
        const chainAssets = assets.filter((a) => a.chainId === chain.id);
        rows.push({
          keyId: key.id,
          address: ltcAdapter.deriveAddress(pubKeyHex, { testnet }),
          addressType: "ltc",
          btcAddrType: "segwit",
          label: `${chain.displayName} (SegWit)`,
          chain,
          assets: chainAssets,
        });
        rows.push({
          keyId: key.id,
          address: publicKeyToLtcLegacyAddress(pubKeyHex, testnet),
          addressType: "ltc",
          btcAddrType: "legacy",
          label: `${chain.displayName} (Legacy)`,
          chain,
          assets: chainAssets,
        });
      }
    }

    if (xrpChains.length > 0) {
      const xrpAdapt = getChainAdapter("xrp");
      const xrpAddr = xrpAdapt.deriveAddress(pubKeyHex);
      for (const chain of xrpChains) {
        const chainAssets = assets.filter((a) => a.chainId === chain.id);
        rows.push({
          keyId: key.id,
          address: xrpAddr,
          addressType: "xrp",
          label: chain.displayName,
          chain,
          assets: chainAssets,
        });
      }
    }

    const tronChains = chains.filter((c) => c.type === "tron");
    if (tronChains.length > 0) {
      const tronAdapt = getChainAdapter("tron");
      const tronAddr = tronAdapt.deriveAddress(pubKeyHex);
      for (const chain of tronChains) {
        const chainAssets = assets.filter((a) => a.chainId === chain.id);
        rows.push({
          keyId: key.id,
          address: tronAddr,
          addressType: "tron",
          label: chain.displayName,
          chain,
          assets: chainAssets,
        });
      }
    }

    const tonChains = chains.filter((c) => c.type === "ton");
    if (tonChains.length > 0) {
      const tonAdapt = getChainAdapter("ton");
      let tonAddr: string;
      try {
        tonAddr = tonAdapt.deriveAddress(key.eddsaPublicKey);
      } catch {
        tonAddr = "";
      }
      if (tonAddr) {
        for (const chain of tonChains) {
          const chainAssets = assets.filter((a) => a.chainId === chain.id);
          rows.push({
            keyId: key.id,
            address: tonAddr,
            addressType: "ton",
            label: chain.displayName,
            chain,
            assets: chainAssets,
          });
        }
      }
    }

    const xlmChains = chains.filter((c) => c.type === "xlm");
    if (xlmChains.length > 0) {
      const xlmAdapt = getChainAdapter("xlm");
      const xlmAddr = xlmAdapt.deriveAddress(key.eddsaPublicKey);
      for (const chain of xlmChains) {
        const chainAssets = assets.filter((a) => a.chainId === chain.id);
        rows.push({
          keyId: key.id,
          address: xlmAddr,
          addressType: "xlm",
          label: chain.displayName,
          chain,
          assets: chainAssets,
        });
      }
    }

    const algoChains = chains.filter((c) => c.type === "algo");
    if (algoChains.length > 0) {
      const algoAdapt = getChainAdapter("algo");
      let algoAddr: string;
      try {
        algoAddr = algoAdapt.deriveAddress(key.eddsaPublicKey);
      } catch {
        algoAddr = "";
      }
      if (algoAddr) {
        for (const chain of algoChains) {
          const chainAssets = assets.filter((a) => a.chainId === chain.id);
          rows.push({
            keyId: key.id,
            address: algoAddr,
            addressType: "algo",
            label: chain.displayName,
            chain,
            assets: chainAssets,
          });
        }
      }
    }

    // ADA (Cardano) — Ed25519
    const adaChains = chains.filter((c) => c.type === "ada");
    if (adaChains.length > 0) {
      const adaAdapt = getChainAdapter("ada");
      for (const chain of adaChains) {
        const testnet = chain.name.includes("TESTNET") || chain.name.includes("PREPROD");
        let adaAddr: string;
        try {
          adaAddr = adaAdapt.deriveAddress(key.eddsaPublicKey, { testnet });
        } catch {
          adaAddr = "";
        }
        if (adaAddr) {
          const chainAssets = assets.filter((a) => a.chainId === chain.id);
          rows.push({
            keyId: key.id,
            address: adaAddr,
            addressType: "ada",
            label: chain.displayName,
            chain,
            assets: chainAssets,
          });
        }
      }
    }
  }
  for (const r of rows) registerWalletAddress(r.address, r.keyId);
  return rows;
}
